import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../services/store';
import { UserRole } from '../../types';
import { LayoutDashboard, Music, Users, Settings, Library, Building, ChevronLeft, ChevronRight, LogOut, Moon, Sun, AlertTriangle, X, Cloud, Video, FileText, BookOpen, ListMusic } from 'lucide-react';
import { ProfileSettings } from '../user/ProfileSettings.tsx';

interface SidebarProps {
    isCollapsed: boolean;
    setIsCollapsed: (val: boolean) => void;
    onCloseMobile?: () => void;
    fullWidth?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, setIsCollapsed, onCloseMobile, fullWidth = false }) => {
    const { user, originalUser, stopImpersonation, t, logout, theme, toggleTheme, updateProfile, language, setLanguage, settings, currentChurch } = useApp();
    const [showProfileModal, setShowProfileModal] = useState(false);

    // Router hooks
    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = location.pathname;

    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                // Only handle clicks outside for mobile menu if needed
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNavigation = useCallback((path: string) => {
        navigate(path);
        if (onCloseMobile) onCloseMobile();
    }, [navigate, onCloseMobile]);

    const NavItem = ({ path, icon: Icon, label }: { path: string, icon: any, label: string }) => {
        const isActive = currentPath === path || (path !== '/' && currentPath.startsWith(path));

        return (
            <button
                onClick={() => handleNavigation(path)}
                title={isCollapsed ? label : ''}
                className={`w-full flex items-center gap-3 px-3 py-3 text-sm font-medium transition-colors rounded-lg mb-1
          ${isActive
                    ? 'bg-brand-500/10 text-brand-500 dark:bg-terracotta-500/20 dark:text-sage-500'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white'
                }
          ${isCollapsed ? 'justify-center' : ''}
        `}
            >
                <Icon size={20} flexshrink={0} />
                {!isCollapsed && <span>{label}</span>}
            </button>
        );
    };

    // RBAC Logic
    const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
    const isChurchAdmin = isSuperAdmin || user?.role === UserRole.CHURCH_ADMIN;
    const isEditor = isChurchAdmin || user?.role === UserRole.EDITOR;

    // Get church name for display
    const getChurchName = () => {
        if (isSuperAdmin) return 'Super Admin';
        if (currentChurch) return currentChurch.name;
        if (user?.churchId) return 'Loading Church...';
        return 'No Church';
    };

    return (
        <>
            <div className={`${isCollapsed ? 'w-20' : fullWidth ? 'w-full' : 'w-64'} h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col flex-shrink-0 transition-all duration-300 relative z-10`}>

                {/* Mobile Close Button */}
                {onCloseMobile && (
                    <button
                        onClick={onCloseMobile}
                        className="absolute right-4 top-6 p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white lg:hidden z-20"
                    >
                        <X size={24} />
                    </button>
                )}

                {/* Toggle Button (Desktop Only) */}
                {!onCloseMobile && (
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="absolute -right-3 top-8 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-full p-1 shadow-md text-gray-600 hover:text-brand-500 dark:text-gray-400 z-20 hidden lg:block"
                    >
                        {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                    </button>
                )}

                {/* Header */}
                <button onClick={() => navigate('/dashboard')} className="p-6 flex items-center w-full text-left">
                    <img src="/logo.svg" alt="ChurchCast Logo" className="w-8 h-8 object-contain flex-shrink-0" />
                    {!isCollapsed && <h1 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight whitespace-nowrap ml-3">ChurchCast</h1>}
                </button>

                {/* Simulation Banner */}
                {originalUser && !isCollapsed && (
                    <div className="px-4 mb-2">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 text-red-600 font-bold text-xs uppercase tracking-wide mb-1">
                                <AlertTriangle size={12} /> {t.nav.simulating}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-white truncate mb-2">
                                As: {user?.name}
                            </div>
                            <button
                                onClick={stopImpersonation}
                                className="w-full py-1 text-xs bg-terracotta-500 text-white rounded hover:bg-terracotta-600 transition-colors"
                            >
                                {t.nav.stopSimulating}
                            </button>
                        </div>
                    </div>
                )}
                {originalUser && isCollapsed && (
                    <button onClick={stopImpersonation} className="mx-auto mb-2 w-10 h-10 bg-terracotta-500 text-white rounded-full flex items-center justify-center" title={t.nav.stopSimulating}>
                        <AlertTriangle size={16} />
                    </button>
                )}

                <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto scrollbar-hide">
                    {/* Accessible to ALL roles */}
                    <NavItem path="/" icon={LayoutDashboard} label={t.nav.dashboard} />
                    <NavItem path="/playlists" icon={ListMusic} label={t.nav.playlists} />

                    {/* EDITOR and above */}
                    {isEditor && (
                        <>
                            {settings?.isMaterialEnabled && (
                                <>
                                    {/* Show base folder as "Material" if no extra base folders configured */}
                                    {(!settings.baseFolders || settings.baseFolders.length === 0) && settings.baseFolder?.id && (
                                        <NavItem path="/material" icon={Cloud} label={t.nav.material} />
                                    )}
                                    
                                    {/* Show default cloud files if no base folder at all */}
                                    {(!settings.baseFolders || settings.baseFolders.length === 0) && !settings.baseFolder?.id && (
                                        <NavItem path="/material" icon={Cloud} label={t.nav.material} />
                                    )}
                                    
                                    {/* Dynamic base folder navigation items */}
                                    {settings.baseFolders && settings.baseFolders.length > 0 && settings.baseFolders.map((baseFolder: any, index: number) => (
                                        <NavItem 
                                            key={index} 
                                            path={`/material/${index}`} 
                                            icon={Cloud} 
                                            label={baseFolder.title || `Cloud Files ${index + 1}`}
                                        />
                                    ))}
                                </>
                            )}
                            {settings?.isWorshipEnabled && (
                                <NavItem path="/lobpreis" icon={Music} label={t.nav.worship} />
                            )}
                        </>
                    )}

                    {/* CHURCH_ADMIN and above */}
                    {isChurchAdmin && (
                        <NavItem path="/benutzer" icon={Users} label={t.nav.users} />
                    )}

                    {isChurchAdmin && (
                        <NavItem path="/einstellungen" icon={Settings} label={t.nav.settings} />
                    )}

                    {/* SUPER_ADMIN only */}
                    {isSuperAdmin && (
                        <>
                            <div className={`my-2 border-t border-gray-200 dark:border-gray-800 ${isCollapsed ? 'mx-2' : ''}`} />
                            <NavItem path="/kirchen" icon={Building} label={t.nav.churches} />
                            <NavItem path="/logs" icon={FileText} label={t.nav.logs} />
                        </>
                    )}
                </nav>

                {/* User Section */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-800 space-y-3">
                    {/* User Profile Button */}
                    <div className="relative group">
                        <button
                            onClick={() => setShowProfileModal(true)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-800 ${isCollapsed ? 'justify-center' : ''}`}
                        >
                            {/* User Icon */}
                            <div className="relative flex-shrink-0">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-500 to-brand-600 dark:from-terracotta-500 dark:to-terracotta-600 text-white flex items-center justify-center font-bold text-sm shadow-lg">
                                    {user?.name.charAt(0)}
                                </div>
                            </div>

                            {/* User Info */}
                            {!isCollapsed && (
                                <div className="flex-1 text-left min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                        {user?.name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {getChurchName()}
                                    </p>
                                </div>
                            )}
                        </button>

                        {/* Logout Icon - Desktop: flies in from right on hover, Mobile: always visible at right */}
                        <button
                            onClick={logout}
                            className={`
                                absolute flex items-center justify-center
                                text-white bg-terracotta-500
                                hover:bg-red-500
                                rounded-full transition-all duration-300 cursor-pointer
                                ${isCollapsed
                                    ? `w-10 h-10 right-1/2 translate-x-1/2 top-1/2 -translate-y-1/2 ${onCloseMobile ? '' : 'opacity-0 group-hover:opacity-100'}`
                                    : `w-8 h-8 right-3 top-1/2 -translate-y-1/2 ${onCloseMobile ? '' : 'opacity-0 translate-x-4 group-hover:opacity-100 group-hover:translate-x-0'}`
                                }
                            `}
                            title={t.nav.logout}
                        >
                            <LogOut size={isCollapsed ? 20 : 16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Profile Settings Modal */}
            <ProfileSettings
                isOpen={showProfileModal}
                onClose={() => setShowProfileModal(false)}
                user={user}
                language={language}
                theme={theme}
                t={t}
                onLanguageChange={setLanguage}
                onThemeToggle={toggleTheme}
                onProfileUpdate={updateProfile}
            />
        </>
    );
};